/**
 * LaTeX delimiter normalization.
 *
 * remark-math only understands `$…$` and `$$…$$`. Models reach for `\(…\)`,
 * `\[…\]`, and bare `\begin{align}` environments constantly, and when they do
 * the reader gets raw backslashes instead of an equation. Rather than fight the
 * model, we rewrite the delimiters before parsing.
 *
 * The scanner skips fenced blocks and inline code, so a literal `\(` inside a
 * code sample survives untouched.
 */

/** Environments that are display math in their own right. */
const DISPLAY_ENVIRONMENTS = [
  "equation",
  "equation*",
  "align",
  "align*",
  "aligned",
  "gather",
  "gather*",
  "multline",
  "multline*",
  "split",
  "cases",
  "matrix",
  "pmatrix",
  "bmatrix",
  "vmatrix",
  "array",
];

export function normalizeMath(markdown: string): string {
  return splitProtectedRegions(markdown).map(renderRegion).join("");
}

function renderRegion(region: Region): string {
  switch (region.kind) {
    case "code":
    case "inline-math":
      return region.text;
    case "display-math":
      return refence(region.text, region.prefix);
    case "prose":
      return convert(region.text);
  }
}

/**
 * Put a `$$` block's delimiters on their own lines.
 *
 * remark-math only emits a *display* node when the fences stand alone;
 * `$$x$$` written on a single line — which models do constantly — parses as
 * inline math and renders at text size, mid-sentence.
 *
 * `prefix` is the blockquote marker chain of the line the block opens on.
 * Inside a callout the emitted lines must carry it, or the equation escapes
 * the block — and, worse, the stray `>` characters end up rendered as part of
 * the mathematics.
 */
function refence(block: string, prefix: string): string {
  const inner = block.slice(2, -2);

  // Already fenced across its own lines: remark-math handles it, and rewriting
  // would only risk pulling the surrounding markers inside.
  if (inner.includes("\n")) return block;

  const body = inner.trim();
  if (body.length === 0) return block;

  return prefix
    ? `$$\n${prefix}${body}\n${prefix}$$`
    : `\n\n$$\n${body}\n$$\n\n`;
}

/**
 * The blockquote markers opening the line at `index`, e.g. `"> "`.
 *
 * Everything this file emits has to reproduce them, since markdown strips them
 * before the math is ever parsed.
 */
export function linePrefixAt(text: string, index: number): string {
  const lineStart = text.lastIndexOf("\n", Math.max(index - 1, 0)) + 1;
  const before = text.slice(lineStart, index);
  return /^[ \t]*(?:>[ \t]?)+/.exec(before)?.[0] ?? "";
}

/** Drop blockquote markers from every line of a captured body. */
function stripQuoteMarkers(body: string): string {
  return body
    .split("\n")
    .map((line) => line.replace(/^[ \t]*(?:>[ \t]?)+/, ""))
    .join("\n");
}

/** Re-apply a prefix to every line of a multi-line replacement. */
const withPrefix = (body: string, prefix: string): string =>
  body
    .split("\n")
    .map((line) => `${prefix}${line}`)
    .join("\n");

type RegionKind =
  /** Code — never touched. */
  | "code"
  /** Already-delimited inline math — contents protected, delimiters kept. */
  | "inline-math"
  /** A `$$…$$` block — contents protected, but re-fenced onto its own lines. */
  | "display-math"
  /** Ordinary prose, where delimiter rewriting happens. */
  | "prose";

interface Region {
  readonly text: string;
  readonly kind: RegionKind;
  /** Blockquote markers opening the line this region starts on. */
  readonly prefix: string;
}

/**
 * Split into alternating prose and code regions.
 *
 * An unterminated fence — normal while a message is still streaming — swallows
 * the rest of the text as code, which is the safe reading: it stops us
 * rewriting delimiters inside a block that turns out to be a plot spec.
 */
function splitProtectedRegions(text: string): readonly Region[] {
  const regions: Region[] = [];
  let index = 0;
  let prose = "";

  const flush = () => {
    if (prose) regions.push({ text: prose, kind: "prose", prefix: "" });
    prose = "";
  };

  while (index < text.length) {
    const match =
      matchFence(text, index) ??
      matchInlineCode(text, index) ??
      matchExistingMath(text, index);

    if (match) {
      flush();
      regions.push({
        text: match.text,
        kind: match.kind,
        prefix: linePrefixAt(text, index),
      });
      index = match.end;
      continue;
    }

    prose += text[index];
    index += 1;
  }

  flush();
  return regions;
}

interface Match {
  readonly text: string;
  readonly end: number;
  readonly kind: RegionKind;
}

function matchFence(text: string, index: number): Match | null {
  // A fence must start a line.
  if (index !== 0 && text[index - 1] !== "\n") return null;

  const opener = /^([ \t]*)(`{3,}|~{3,})/.exec(text.slice(index));
  if (!opener) return null;

  const marker = opener[2]!;
  const bodyStart = index + opener[0].length;
  const closer = new RegExp(`\\n[ \\t]*${marker[0]}{${marker.length},}[ \\t]*(?:\\n|$)`);
  const rest = text.slice(bodyStart);
  const found = closer.exec(rest);

  const end = found ? bodyStart + found.index + found[0].length : text.length;
  return { text: text.slice(index, end), end, kind: "code" };
}

function matchInlineCode(text: string, index: number): Match | null {
  if (text[index] !== "`") return null;

  const ticks = /^`+/.exec(text.slice(index))![0];
  const closeIndex = text.indexOf(ticks, index + ticks.length);
  if (closeIndex === -1) return null;

  const end = closeIndex + ticks.length;
  return { text: text.slice(index, end), end, kind: "code" };
}

/**
 * Math that is already delimited with dollars. Protecting it wholesale is what
 * keeps a `\begin{align}` that sits *inside* a `$$` block from being wrapped a
 * second time.
 */
function matchExistingMath(text: string, index: number): Match | null {
  if (text[index] !== "$") return null;

  if (text.startsWith("$$", index)) {
    const close = text.indexOf("$$", index + 2);
    if (close === -1) return null;
    return {
      text: text.slice(index, close + 2),
      end: close + 2,
      kind: "display-math",
    };
  }

  // Inline math never spans a blank line; a lone `$` is usually currency.
  const rest = text.slice(index + 1);
  const close = rest.search(/(?<!\\)\$/);
  if (close === -1) return null;

  const body = rest.slice(0, close);
  if (body.includes("\n\n")) return null;

  const end = index + 1 + close + 1;
  return { text: text.slice(index, end), end, kind: "inline-math" };
}

function convert(text: string): string {
  let output = text;

  /**
   * Display first: \[ … \] becomes a block of display math.
   *
   * The newlines inside the `$$` fence are load-bearing. remark-math only
   * produces a *display* node when the delimiters sit on their own lines;
   * `$$x$$` on one line parses as inline math and renders at text size.
   */
  output = output.replace(
    /(?<!\\)\\\[([\s\S]*?)(?<!\\)\\\]/g,
    (_match: string, body: string, offset: number) => {
      // A display block inside a callout keeps the callout's markers, and its
      // body must be cleaned of the ones markdown already accounted for.
      const prefix = linePrefixAt(output, offset);
      const cleaned = stripQuoteMarkers(body).trim();
      return prefix
        ? `$$\n${withPrefix(cleaned, prefix)}\n${prefix}$$`
        : `\n\n$$\n${cleaned}\n$$\n\n`;
    },
  );

  // Inline: \( … \)
  output = output.replace(
    /(?<!\\)\\\(([\s\S]*?)(?<!\\)\\\)/g,
    (_match, body: string) => `$${body.trim()}$`,
  );

  output = wrapEnvironments(output);

  return output;
}

/**
 * Wrap a bare `\begin{align}…\end{align}` in `$$`. KaTeX renders the
 * environment happily, but remark-math never hands it over unless it is
 * delimited, so it would otherwise print as source.
 */
function wrapEnvironments(text: string): string {
  const names = DISPLAY_ENVIRONMENTS.map((name) =>
    name.replace(/[*]/g, "\\*"),
  ).join("|");
  const pattern = new RegExp(
    `(?<!\\$)\\\\begin\\{(${names})\\}([\\s\\S]*?)\\\\end\\{\\1\\}(?!\\$)`,
    "g",
  );

  return text.replace(pattern, (match: string, ..._rest: unknown[]) => {
    const offset = _rest[_rest.length - 2] as number;
    const prefix = linePrefixAt(text, offset);
    const cleaned = stripQuoteMarkers(match).trim();
    return prefix
      ? `$$\n${withPrefix(cleaned, prefix)}\n${prefix}$$`
      : `\n\n$$\n${cleaned}\n$$\n\n`;
  });
}
